/**
 * Merci : la rotation de la ligne de réassurance, et ses points cliquables.
 *
 * Même principe que la réassurance du tiroir panier d'avc : un élément
 * personnalisé autonome qui passe d'une phrase à l'autre, des points qui
 * choisissent la phrase, une pause au survol et au focus.
 *
 * --- Pourquoi la rotation a quitté le CSS
 *
 * Elle était entièrement en CSS jusqu'au 2026-09-24 : une famille
 * d'images-clés par nombre de phrases, décalées d'un `animation-delay`. Le
 * procédé ne sait pas **sauter** à une phrase — il déroule un temps, il ne
 * connaît pas d'état — et sous `prefers-reduced-motion`, où l'animation est
 * coupée, il n'y aurait plus rien à piloter du tout. Des points cliquables
 * demandent un état : c'est donc le script qui dit quelle phrase est visible,
 * et le CSS ne fait plus que le fondu.
 *
 * --- La pause
 *
 * Au survol et au focus, comme sur avc. Un contenu qui défile seul doit pouvoir
 * s'arrêter (WCAG 2.2.2) : qui pointe une phrase ou tabule jusqu'aux points est
 * en train de lire, et la phrase ne doit pas changer sous ses yeux.
 *
 * Sous `prefers-reduced-motion`, pas de défilement automatique du tout, mais
 * les points restent cliquables : c'est même le seul moyen, alors, de voir les
 * phrases suivantes.
 *
 * --- Sans ce script
 *
 * La première phrase reste affichée, marquée active par le serveur. On perd la
 * rotation, pas la mise en page.
 */

class MerciReassurance extends HTMLElement {
  #index = 0;
  #minuterie = null;
  #survol = false;
  #focus = false;

  connectedCallback() {
    this.phrases = [...this.querySelectorAll('.merci-reassurance__item')];
    this.points = [...this.querySelectorAll('.merci-reassurance__dot')];
    if (this.phrases.length < 2) return;

    this.delai = (Number(this.dataset.duration) || 4) * 1000;
    this.reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Le clic ne relance pas la minuterie : il arrive toujours pendant une
    // pause — le pointeur est sur le bloc, ou le focus sur le point —, et la
    // rotation repartira d'un compte neuf à la sortie. La relancer ici l'aurait
    // fait reprendre sous le pointeur, pendant qu'on lit la phrase choisie :
    // c'est ce que faisait le modèle d'avc.
    for (const point of this.points) {
      point.addEventListener('click', () => this.#afficher(Number(point.dataset.index)));
    }

    this.addEventListener('pointerenter', () => {
      this.#survol = true;
      this.#arreter();
    });

    this.addEventListener('pointerleave', () => {
      this.#survol = false;
      this.#demarrer();
    });

    this.addEventListener('focusin', () => {
      this.#focus = true;
      this.#arreter();
    });

    // D'un point à l'autre, le focus reste dans le bloc : pas de reprise.
    this.addEventListener('focusout', (evenement) => {
      this.#focus = this.contains(evenement.relatedTarget);
      this.#demarrer();
    });

    this.#demarrer();
  }

  disconnectedCallback() {
    this.#arreter();
  }

  /** @param {number} index */
  #afficher(index) {
    this.#index = (index + this.phrases.length) % this.phrases.length;

    for (const [rang, phrase] of this.phrases.entries()) {
      const active = rang === this.#index;
      phrase.toggleAttribute('data-active', active);

      // Les phrases masquées sortent de l'arbre d'accessibilité : un lecteur
      // d'écran n'annonce que celle qu'on voit, et les points servent à changer.
      if (active) phrase.removeAttribute('aria-hidden');
      else phrase.setAttribute('aria-hidden', 'true');
    }

    for (const [rang, point] of this.points.entries()) {
      // `aria-current` porte l'information pour les lecteurs d'écran et sert de
      // crochet au CSS : pas de classe en double.
      if (rang === this.#index) point.setAttribute('aria-current', 'true');
      else point.removeAttribute('aria-current');
    }
  }

  #demarrer() {
    if (this.reduit || this.#minuterie || this.#survol || this.#focus) return;
    this.#minuterie = setInterval(() => this.#afficher(this.#index + 1), this.delai);
  }

  #arreter() {
    clearInterval(this.#minuterie);
    this.#minuterie = null;
  }
}

if (!customElements.get('merci-reassurance')) {
  customElements.define('merci-reassurance', MerciReassurance);
}
